import * as amplitude from "@amplitude/analytics-react-native"
import { useNewSubscriptionStepper } from "@common/hooks/subscription/useNewSubscriptionStepper"
import { useInscriptionFunnel } from "../context/InscriptionFunnelContext"
import {
    engagementSavingLabel,
    quoteIsWithoutCommitment,
} from "../utils/formulaChoice"
import { useInscriptionLead } from "./useInscriptionLead"

/**
 * Ce que les règles de `utils/formulaChoice` attendent, assemblé une fois pour
 * les écrans où la formule se compose (l'étape formule, la page du format) :
 * le choix, ses règles, l'interrupteur d'engagement et son suivi. Ces écrans
 * ne doivent différer que par leur mise en page.
 *
 * `formula` est `{ offerType, weeklyHours, hoursAllocation }` tel que l'écran
 * le tient ; `served` ce que le serveur annonce (`noCommitment`, `priceGrid`,
 * `weeklyHoursByOffer`) ; `source` nomme l'écran dans le suivi.
 */
export const useFormulaChoice = ({
    formula,
    subjectIds,
    served,
    saving,
    quote,
    source,
}) => {
    const { state } = useInscriptionFunnel()
    const { funnelStateId } = useInscriptionLead()
    const { generateDefaultSubjectHoursAllocation } =
        useNewSubscriptionStepper()
    const { noCommitment } = served

    // L'engagement DEMANDÉ, tel que le parcours le retient. Sans formule
    // annoncée par le serveur, rien ne se vend sans engagement : ce qui reste
    // en mémoire ne compte pas.
    const askedWithCommitment = noCommitment
        ? state.withCommitment !== false
        : true
    // L'engagement VENDU, celui du devis.
    const soldWithCommitment = !quoteIsWithoutCommitment(quote)

    const choice = {
        ...formula,
        withCommitment: askedWithCommitment,
        committedFormula: state.committedFormula || null,
    }
    const rules = {
        ...served,
        subjectIds,
        splitHours: generateDefaultSubjectHoursAllocation,
    }

    return {
        askedWithCommitment,
        choice,
        rules,
        // `null` : le serveur ne vend pas sans engagement, pas d'interrupteur.
        // Il ne s'estompe que le temps d'un enregistrement dont le devis n'a
        // pas encore suivi. Sa ligne dit ce que l'engagement fait économiser,
        // coupé ou non : coupé, c'est l'économie de la formule que le
        // remettre rendrait.
        commitment: noCommitment
            ? {
                  checked: askedWithCommitment,
                  pending: saving && askedWithCommitment !== soldWithCommitment,
                  savingLabel: engagementSavingLabel(
                      served.priceGrid,
                      choice,
                      rules
                  ),
              }
            : null,
        trackCommitment: (withCommitment) =>
            amplitude.track("inscription_funnel_commitment_toggled", {
                funnel_state_id: funnelStateId,
                with_commitment: withCommitment,
                source,
            }),
    }
}
